import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Briefcase, Calendar, ArrowRight, Filter, ChevronDown, AlertCircle, Bell, Wrench, Car, FileCheck, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AuthModal from '../components/AuthModal';
import PartnerPlacement from '../components/PartnerPlacement';
import VerificationBadge from '../components/VerificationBadge';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';
import { todayYMD, addDaysYMD, formatYMD } from '../utils/dateHelpers';

// Radius options
const RADIUS_OPTIONS = [5, 10, 15, 25, 50];

export default function PublicSearch() {
  const navigate = useNavigate();
  const { get } = useSiteContent();
  
  // Search form state
  const [trade, setTrade] = useState('');
  const [location, setLocation] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [radius, setRadius] = useState(15);
  
  // Data state
  const [trades, setTrades] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchParams, setSearchParams] = useState(null);
  
  // Suggestions state (for no exact matches)
  const [suggestions, setSuggestions] = useState(null);
  
  // Filter state (shown on results page)
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    verifiedOnly: false,
    hasCredentials: false,
    hasVan: false,
    hasInsurance: false,
    minRating: 0
  });
  
  // Auth modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    fetchTrades();
    // Set default dates (today + 7 days) using LOCAL calendar dates.
    // Avoid toISOString() which converts to UTC and shifts the date
    // back by 1 day for anyone west of UTC (or during BST).
    const today = todayYMD();
    setDateFrom(today);
    setDateTo(addDaysYMD(today, 7));
  }, []);

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trades`);
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!trade) {
      toast.error(get('search.validation.select_trade', 'Please select a trade'));
      return;
    }
    if (!location.trim()) {
      toast.error(get('search.validation.enter_location', 'Please enter a job location'));
      return;
    }
    if (!dateFrom || !dateTo) {
      toast.error(get('search.validation.select_dates', 'Please select the dates you need work done'));
      return;
    }

    setLoading(true);
    setSearched(true);
    setSuggestions(null);
    setSearchParams({ trade, location, dateFrom, dateTo, radius });

    try {
      const params = new URLSearchParams();
      params.append('public', 'true');
      params.append('trade', trade);
      params.append('location', location);
      params.append('date_from', dateFrom);
      params.append('date_to', dateTo);
      params.append('radius', radius.toString());
      
      // Apply filters if any
      if (filters.verifiedOnly) params.append('verified_only', 'true');
      if (filters.hasCredentials) params.append('has_credentials', 'true');
      if (filters.hasVan) params.append('has_van', 'true');
      if (filters.hasInsurance) params.append('has_insurance', 'true');
      if (filters.minRating > 0) params.append('min_rating', filters.minRating.toString());

      const response = await axios.get(
        `${BACKEND_URL}/api/tradespeople/availability-search?${params}`
      );
      
      setResults(response.data.results || []);
      setSuggestions(response.data.suggestions || null);
      
    } catch (error) {
      // Fallback to basic search if availability search not available
      try {
        const params = new URLSearchParams();
        params.append('public', 'true');
        params.append('trade', trade);
        params.append('location', location);

        const response = await axios.get(
          `${BACKEND_URL}/api/tradespeople/public-search?${params}`
        );
        
        setResults(response.data || []);
      } catch (fallbackError) {
        toast.error('Search failed. Please try again.');
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = (userId, actionType) => {
    setPendingAction({ type: actionType, userId });
    setShowAuthModal(true);
  };

  const handleAuthSuccess = (userData, action) => {
    if (action?.userId) {
      navigate(`/profile/${action.userId}`);
    } else {
      navigate('/employer/dashboard');
    }
  };

  const handleNotifyMe = () => {
    setPendingAction({ type: 'notify', searchParams });
    setShowAuthModal(true);
  };

  // Try wider radius
  const handleWiderRadius = () => {
    const currentIndex = RADIUS_OPTIONS.indexOf(radius);
    if (currentIndex < RADIUS_OPTIONS.length - 1) {
      setRadius(RADIUS_OPTIONS[currentIndex + 1]);
      // Auto-search with new radius
      setTimeout(() => {
        document.getElementById('search-btn').click();
      }, 100);
    }
  };

  // Try flexible dates — ±3 days around the current window, kept on
  // the local calendar so the range never drifts across timezones.
  const handleFlexibleDates = () => {
    setDateFrom(addDaysYMD(dateFrom, -3));
    setDateTo(addDaysYMD(dateTo, 3));
    setTimeout(() => {
      document.getElementById('search-btn').click();
    }, 100);
  };

  const getAvailabilityColor = (label) => {
    if (label?.toLowerCase().includes('now')) return 'text-green-600 bg-green-50 border-green-200';
    if (label?.toLowerCase().includes('from')) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const filteredResults = results.filter(person => {
    if (filters.minRating > 0 && (person.rating_avg || 0) < filters.minRating) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-xl font-bold text-slate-900"
          >
            <img src="/logo.png" alt="OnsiteHand" className="h-8 w-8 rounded-lg" />
            <span>OnsiteHand</span>
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-slate-600 hover:text-slate-900 font-medium px-4 py-2"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register?type=employer')}
              className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 py-10 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="search-title">
              Find available trades for your job
            </h1>
            <p className="text-slate-400 text-sm">
              Tell us what you need and when — we'll match you with available tradespeople
            </p>
          </div>

          <form onSubmit={handleSearch} className="bg-white rounded-xl p-5 shadow-lg" data-testid="job-search-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Trade Selection - Required */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Trade required <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <select
                    value={trade}
                    onChange={(e) => setTrade(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
                    required
                    data-testid="search-trade-select"
                  >
                    <option value="">Select a trade...</option>
                    {trades.map((t) => (
                      <option key={t.trade_id} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Location - Required */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Job location <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Postcode or town (e.g., KT1, Guildford)"
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                    data-testid="search-location-input"
                  />
                </div>
              </div>

              {/* Date From - Required */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Date from <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    min={todayYMD()}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                    data-testid="search-date-from"
                  />
                </div>
              </div>

              {/* Date To - Required */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Date to <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    min={dateFrom || todayYMD()}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                    data-testid="search-date-to"
                  />
                </div>
              </div>
            </div>

            {/* Radius Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Search radius
              </label>
              <div className="flex flex-wrap gap-2">
                {RADIUS_OPTIONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRadius(r)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      radius === r
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`radius-${r}`}
                  >
                    {r} miles
                  </button>
                ))}
              </div>
            </div>

            {/* Search Button */}
            <button
              id="search-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="search-availability-btn"
            >
              <Search className="h-5 w-5" />
              {loading ? get('search.form.searching', 'Searching...') : get('search.form.search_btn', 'Search Availability')}
            </button>
          </form>

          {/* Trust Line */}
          <p className="text-center text-slate-400 text-xs mt-4">
            {get('search.free_preview_note', 'OnSiteHand is a free marketplace — search, view full profiles and message tradespeople at no cost.')}
          </p>
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {!searched ? (
          /* Initial State - No search yet */
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-10 w-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              {get('search.page.title', 'Find Tradespeople')}
            </h2>
            <p className="text-slate-500 max-w-md mx-auto">
              Fill in the form above with your job details and we'll show you available tradespeople who can help
            </p>
          </div>
        ) : loading ? (
          /* Loading State */
          <div className="text-center py-16">
            <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Finding available {trade}s near {location}...</p>
          </div>
        ) : filteredResults.length === 0 ? (
          /* No Results State - Smart suggestions */
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">
                No exact matches for those dates
              </h2>
              <p className="text-slate-500 mb-6">
                We couldn't find a {trade} available in {location} between {formatYMD(dateFrom, { day: 'numeric', month: 'short', year: 'numeric' })} and {formatYMD(dateTo, { day: 'numeric', month: 'short', year: 'numeric' })} within {radius} miles.
              </p>

              {/* Suggestions */}
              <div className="space-y-3 mb-6">
                <p className="text-sm text-slate-600 font-medium">Try these options:</p>
                
                <button
                  onClick={handleFlexibleDates}
                  className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                  data-testid="try-flexible-dates"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium text-slate-800">Try flexible dates</p>
                      <p className="text-xs text-slate-500">Expand your date range by a few days</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400" />
                </button>

                {radius < 50 && (
                  <button
                    onClick={handleWiderRadius}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                    data-testid="try-wider-radius"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="font-medium text-slate-800">Search wider area</p>
                        <p className="text-xs text-slate-500">Increase radius to {RADIUS_OPTIONS[RADIUS_OPTIONS.indexOf(radius) + 1]} miles</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-400" />
                  </button>
                )}

                <button
                  onClick={handleNotifyMe}
                  className="w-full flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-left"
                  data-testid="notify-me-btn"
                >
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="font-medium text-slate-800">Get notified when available</p>
                      <p className="text-xs text-slate-500">We'll alert you when a {trade} becomes available</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400" />
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 pt-6">
                <p className="text-sm text-slate-500 mb-3">Or post your requirement directly</p>
                <button
                  onClick={() => navigate('/register?type=employer')}
                  className="btn-primary"
                  data-testid="post-requirement-btn"
                >
                  Post Job Requirement
                </button>
              </div>
            </div>

            {/* Partner Placement - No Results */}
            <div className="mt-6">
              <PartnerPlacement 
                placement="no-results" 
                userType="employer" 
                region={location}
                variant="banner"
              />
            </div>
          </div>
        ) : (
          /* Results Found */
          <>
            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {filteredResults.length} {trade}{filteredResults.length !== 1 ? 's' : ''} available
                </h2>
                <p className="text-sm text-slate-500">
                  Near {location} • {formatYMD(dateFrom, { day: 'numeric', month: 'short' })} - {formatYMD(dateTo, { day: 'numeric', month: 'short' })}
                </p>
                <p className="text-xs text-slate-500 mt-1" data-testid="availability-disclaimer">
                  Availability is provided by the worker and may change. Confirm it directly before agreeing work.
                </p>
              </div>
              
              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  showFilters ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4" />
                Filters
                {Object.values(filters).some(v => v) && (
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                )}
              </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="filters-panel">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer" title="Show only tradespeople where OnSiteHand has verified at least one submitted credential. Not a guarantee of workmanship, conduct, availability or suitability.">
                    <input
                      type="checkbox"
                      checked={filters.verifiedOnly}
                      onChange={(e) => setFilters({...filters, verifiedOnly: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded border-slate-300 focus:ring-orange-500"
                      data-testid="filter-credentials-verified-only"
                    />
                    <span className="text-sm text-slate-700 flex items-center gap-1">
                      <Shield className="h-4 w-4 text-green-500" />
                      Credentials verified only
                    </span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.hasCredentials}
                      onChange={(e) => setFilters({...filters, hasCredentials: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded border-slate-300 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700 flex items-center gap-1">
                      <FileCheck className="h-4 w-4 text-blue-500" />
                      Has credentials
                    </span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.hasVan}
                      onChange={(e) => setFilters({...filters, hasVan: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded border-slate-300 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700 flex items-center gap-1">
                      <Car className="h-4 w-4 text-slate-500" />
                      Has van
                    </span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.hasInsurance}
                      onChange={(e) => setFilters({...filters, hasInsurance: e.target.checked})}
                      className="w-4 h-4 text-orange-500 rounded border-slate-300 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700 flex items-center gap-1">
                      <Shield className="h-4 w-4 text-purple-500" />
                      Insured
                    </span>
                  </label>
                  
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-400" />
                    <select
                      value={filters.minRating}
                      onChange={(e) => setFilters({...filters, minRating: parseFloat(e.target.value)})}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="0">Any rating</option>
                      <option value="3">3+ stars</option>
                      <option value="4">4+ stars</option>
                      <option value="4.5">4.5+ stars</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Results Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResults.map((person) => (
                <div
                  key={person.user_id}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-orange-200 transition-all"
                  data-testid={`result-card-${person.user_id}`}
                >
                  {/* Trade & Match Badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Wrench className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">
                          {person.trade || 'Tradesperson'}
                        </h3>
                        <div className="flex items-center gap-1 text-slate-500 text-sm">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>
                            {typeof person.distance === 'number'
                              ? `${person.distance.toFixed(1)} miles away`
                              : (person.area_label || 'Local')}
                          </span>
                        </div>
                      </div>
                    </div>
                    {person.match_score && (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium">
                        {person.match_score}% match
                      </span>
                    )}
                  </div>

                  {/* Availability Badge */}
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium mb-3 border ${getAvailabilityColor(person.availability_label)}`}>
                    <Calendar className="h-4 w-4" />
                    {person.availability_label || 'Available'}
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    {person.rating_avg > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                        <span className="font-semibold text-slate-900">{person.rating_avg.toFixed(1)}</span>
                        {person.review_count > 0 && (
                          <span className="text-slate-400">({person.review_count})</span>
                        )}
                      </div>
                    )}
                    {person.verification_summary && person.verification_summary !== 'none' && (
                      <VerificationBadge
                        summary={person.verification_summary}
                        size="sm"
                        testIdPrefix={`search-card-${person.user_id}`}
                      />
                    )}
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => handleContactClick(person.user_id, 'view')}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid={`view-profile-btn-${person.user_id}`}
                  >
                    View Profile & Hire
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Sign Up Prompt */}
            <div className="mt-10 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                Ready to hire?
              </h3>
              <p className="text-slate-300 mb-6 max-w-lg mx-auto">
                Create a free account to view full profiles, check availability in detail, and contact tradespeople directly.
              </p>
              <button
                onClick={() => navigate('/register?type=employer')}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
                data-testid="create-account-btn"
              >
                Create Free Account
              </button>
              <p className="text-slate-400 text-xs mt-4">
                OnSiteHand is a free marketplace — search, view full profiles and message tradespeople at no cost.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        open={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPendingAction(null);
        }}
        onSuccess={handleAuthSuccess}
        pendingAction={pendingAction}
        message={
          pendingAction?.type === 'notify' 
            ? get('search.cta.create_account_notify', 'Create an account to get notified when trades become available')
            : get('search.cta.create_account_view', 'Create an account to view full profiles and contact tradespeople')
        }
      />
    </div>
  );
}
